// require('dotenv').config()
import React, { useEffect, useMemo, useRef, useState } from 'react'
import "./menu.css"
import { FaUncharted, FaSignOutAlt, FaPuzzlePiece } from "react-icons/fa";
import { useAuth } from '../../context/AuthProvider';
import { useCustomer } from '../../context/CustomerProvider';
import { useHistory, Link } from "react-router-dom"
import { AutoComplete, Badge, Layout, Typography, Space, message, Modal, Input } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

const { Header } = Layout;

const PENDING_REVIEW_FIELD_LABELS = {
    policy: "Policy",
    faqs: "FAQ's",
    tone: "Tone",
};

export default function Menu({ logic, page }){

    const { logout, currentUser, brandId, brands, brandIdLoaded, setSelectedBrand } = useAuth()
    const { pendingReviewsList, refreshPendingReviews } = useCustomer()
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const [isSwitchBrandModalOpen, setIsSwitchBrandModalOpen] = useState(false)
    const [brandSearch, setBrandSearch] = useState("")
    const brandSearchInputRef = useRef(null)
    const history = useHistory()

    const totalPendingCount = useMemo(
      () => pendingReviewsList.reduce((sum, b) => sum + b.pendingFields.length, 0),
      [pendingReviewsList]
    );

    useEffect(() => {
      if (!brands?.length || !currentUser) {
        // Signed-in user with no brands (or signed out): clear any stale list.
        refreshPendingReviews([]);
        return;
      }
      const ids = brands.map(b => b.id);
      refreshPendingReviews(ids);
      const interval = setInterval(() => refreshPendingReviews(ids), 60000);
      return () => clearInterval(interval);
    }, [brands, currentUser, refreshPendingReviews]);

    const handleLogout = async () => {
        try {
          setError("")
          setLoading(true)
          await logout()
          history.push("/login")
        } catch {
          setError("Failed to log out")
        }
        setLoading(false)
      }

    const settingsActive = page === "Settings"
    const analyticsActive = page === "Analytics"

    const userEmail = currentUser?.email ?? "—"
    const userUid = currentUser?.uid ?? "—"

    const selectedBrandName = useMemo(() => {
        if (currentUser && !brandIdLoaded) return "Loading brand…"
        if (!brandId) return "No brand selected"
        const match = brands?.find((b) => b.id === brandId)
        return match?.name ?? "—"
    }, [brandId, brands, brandIdLoaded, currentUser])

    const selectedBrandIdDisplay = useMemo(() => {
        if (currentUser && !brandIdLoaded) return "—"
        if (!brandId) return "—"
        return brandId
    }, [brandId, brandIdLoaded, currentUser])

    useEffect(() => {
        const match = brands?.find((b) => b.id === brandId)
        setBrandSearch(match?.name ?? "")
    }, [brandId, brands])

    const copyToClipboard = async (text, successDescription) => {
        if (!text || text === "—") return
        try {
            await navigator.clipboard.writeText(text)
            message.success(successDescription)
        } catch {
            message.error("Could not copy")
        }
    }

    const selectBrandSearchInput = () => {
        window.requestAnimationFrame(() => {
            brandSearchInputRef.current?.select?.()
        })
    }

    const goToField = (bId, field) => {
        if (!PENDING_REVIEW_FIELD_LABELS[field]) return;
        if (bId !== brandId) {
            setSelectedBrand(bId);
        }
        history.push({
            pathname: '/',
            search: `?field=${encodeURIComponent(field)}`,
            state: { scrollField: field, scrollTs: Date.now() },
        });
    }

    const titleNode = analyticsActive ? (
        <Space align="center" size={14} className="crm-page-header__row">
            <span className="crm-page-header__badge" aria-hidden>
                <FaUncharted />
            </span>
            <Typography.Title level={1} className="crm-page-header__heading">
                {page}
            </Typography.Title>
        </Space>
    ) : settingsActive ? (
        <Space align="center" size={14} className="crm-page-header__row">
            <span className="crm-page-header__badge" aria-hidden>
                <FaPuzzlePiece />
            </span>
            <Typography.Title level={1} className="crm-page-header__heading">
                {page}
            </Typography.Title>
        </Space>
    ) : (
        <Typography.Title level={1} className="crm-page-header__heading">
            {page}
        </Typography.Title>
    )

    return (
        <div className="crm-app-shell">
            <aside className='primary' aria-label="Main navigation">
                <div className='primary-pattern' aria-hidden="true" />
                <div className='secondary'>
                    <div className='header'>
                        <div className="crm-brand">
                            <img className='sidebar-logo' src="/logo.svg" alt="" />
                            <span className="crm-brand-text">ICARUS<span className="crm-brand-ai">AI</span></span>
                        </div>
                    </div>
                    <nav className='menu' aria-label="Product">
                        <Link to="/" className={settingsActive ? 'nav-item nav-item--active' : 'nav-item'}>
                            <FaPuzzlePiece className="nav-item__icon" aria-hidden />
                            <span className="nav-item__label">Settings</span>
                            {totalPendingCount > 0 && (
                                <Badge count={totalPendingCount} size="small" className="nav-item__badge" />
                            )}
                        </Link>
                        <Link to="/a" className={analyticsActive ? 'nav-item nav-item--active' : 'nav-item'}>
                            <FaUncharted className="nav-item__icon" aria-hidden />
                            <span className="nav-item__label">Analytics</span>
                        </Link>
                        {pendingReviewsList.length > 0 && (
                            <div className="crm-sidebar-pending-reviews">
                                <div className="crm-sidebar-pending-reviews__header">
                                    <WarningOutlined /> Pending Reviews
                                </div>
                                <ul className="crm-sidebar-pending-reviews__list">
                                    {pendingReviewsList.map(({ brandId: bId, brandName, pendingFields }) => (
                                        <li key={bId} className="crm-sidebar-pending-reviews__brand">
                                            <strong>{brandName}</strong>
                                            <ul>
                                                {pendingFields.map(field => (
                                                    <li key={field}>
                                                        <button
                                                            type="button"
                                                            className="crm-sidebar-pending-reviews__field"
                                                            onClick={() => goToField(bId, field)}
                                                        >
                                                            {PENDING_REVIEW_FIELD_LABELS[field] || field}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div className="crm-sidebar-session" aria-label="Signed-in user and brand">
                            <div className="crm-sidebar-session__section">
                                <div className="crm-sidebar-session__section-title">User</div>
                                <div className="crm-sidebar-session__field">
                                    <span className="crm-sidebar-session__field-value">{userEmail}</span>
                                </div>
                                <div className="crm-sidebar-session__field">
                                    <button
                                        type="button"
                                        className="crm-sidebar-session__id-copy"
                                        onClick={() => copyToClipboard(userUid, "User ID copied")}
                                        disabled={!currentUser?.uid}
                                        aria-label="Copy user ID"
                                        title="Copy to clipboard"
                                    >
                                        {userUid}
                                    </button>
                                </div>
                            </div>

                            <div className="crm-sidebar-session__divider" role="separator" />

                            <div className="crm-sidebar-session__section">
                                <div className="crm-sidebar-session__section-title">Active brand</div>
                                <div className="crm-sidebar-session__field">
                                    <span className="crm-sidebar-session__field-value">{selectedBrandName}</span>
                                </div>
                                <div className="crm-sidebar-session__field">
                                    <button
                                        type="button"
                                        className="crm-sidebar-session__id-copy"
                                        onClick={() => copyToClipboard(brandId, "Brand ID copied")}
                                        disabled={!brandId}
                                        aria-label="Copy brand ID"
                                        title="Copy to clipboard"
                                    >
                                        {selectedBrandIdDisplay}
                                    </button>
                                </div>
                            </div>
                            <div className="crm-sidebar-session__divider" role="separator" />
                            <div className="crm-sidebar-session__section">
                                <div className="crm-sidebar-session__field">
                                    <button
                                        type="button"
                                        className="crm-sidebar-session__switch-btn"
                                        onClick={() => setIsSwitchBrandModalOpen(true)}
                                    >
                                        Switch brand
                                    </button>
                                </div>
                            </div>
                        </div>
                    </nav>
                    <div className='menu-account'>
                        <button onClick={() => handleLogout()} className="button2" disabled={loading} type="button" aria-label="Log out">
                            <FaSignOutAlt aria-hidden />
                        </button>
                    </div>
                </div>
            </aside>

            <div className='logic'>
                <Modal
                    centered
                    title="Switch brand"
                    open={isSwitchBrandModalOpen}
                    onCancel={() => setIsSwitchBrandModalOpen(false)}
                    footer={null}
                >
                    <Typography.Paragraph className="crm-switch-brand-modal__hint">
                        Search by name, then select the brand you want to switch to.
                    </Typography.Paragraph>
                    <AutoComplete
                        className="crm-switch-brand-modal__select"
                        popupClassName="brand-workspace-select-dropdown"
                        options={(brands ?? []).map((brand) => ({
                            value: brand.name,
                            label: brand.name,
                            brandId: brand.id,
                        }))}
                        value={brandSearch}
                        onChange={setBrandSearch}
                        onSelect={async (_name, option) => {
                            await setSelectedBrand(option.brandId)
                            setIsSwitchBrandModalOpen(false)
                        }}
                        filterOption={(input, option) =>
                            String(option?.label ?? '')
                                .toLowerCase()
                                .includes(input.trim().toLowerCase())
                        }
                        onFocus={selectBrandSearchInput}
                        onClick={selectBrandSearchInput}
                        onDropdownVisibleChange={(open) => {
                            if (open) selectBrandSearchInput()
                        }}
                    >
                        <Input
                            ref={brandSearchInputRef}
                            placeholder="Choose a brand"
                            disabled={Boolean(currentUser && !brandIdLoaded)}
                        />
                    </AutoComplete>
                </Modal>

                <Header className="header2 crm-page-header" role="banner">
                    <div className="crm-page-header__inner">
                        {titleNode}
                    </div>
                </Header>
                <div className='logic-in-logic'>
                    {logic}
                </div>
            </div>
        </div>
    )
}
